import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[44ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico whitespace-nowrap"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[22ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Galleria — Finale Scudetto Serie A1 Tigotà 2025/26.
// La galleria si adatta dinamicamente alla lunghezza di `GALLERY_PHOTOS`.
// Per aggiungere/rimuovere scatti basta editare l'array: il render, la lightbox
// e la variazione di ritmo (ogni 5° slot 2x2) si aggiornano automaticamente.
const GALLERY_PHOTOS = [
  // Batch 1 (4 scatti unici)
  "/images/assets/l0oer29x_saveclip.app_670596620_18121573099715693_1474756804845084331_n.jpg",
  "/images/assets/sezgfseh_saveclip.app_670734989_18121573090715693_6182273383405506046_n.jpg",
  "/images/assets/sjeajam2_saveclip.app_670885811_18121573081715693_1593581416539520912_n.jpg",
  "/images/assets/mc1kvul3_saveclip.app_670993130_18121573072715693_2678944927598750613_n.jpg",
  // Batch 2 (5 scatti)
  "/images/assets/hp9k2g8d_saveclip.app_670553589_18121573126715693_7301445274011496980_n.jpg",
  "/images/assets/qkepl5wy_saveclip.app_670839442_18121573144715693_5362893935398057670_n.jpg",
  "/images/assets/07ryzde0_saveclip.app_671801115_18121573141715693_509678157164653695_n.jpg",
  "/images/assets/kn6uxmki_saveclip.app_659276115_18121573117715693_5753584276297381869_n.jpg",
  "/images/assets/ggg9zjtl_saveclip.app_670365208_18121573108715693_2906364508276818829_n.jpg",
  // Batch 3 (5 scatti)
  "/images/assets/b5nxvmm8_saveclip.app_670750806_18121573204715693_2981900608330402339_n.jpg",
  "/images/assets/oy20p57r_saveclip.app_670627337_18121573180715693_3309598718068635346_n.jpg",
  "/images/assets/zjx0mwgx_saveclip.app_670612801_18121573195715693_7717679475105672237_n.jpg",
  "/images/assets/ufc22dlg_saveclip.app_670745102_18121573177715693_7947769459905115964_n.jpg",
  "/images/assets/1x3b29yd_saveclip.app_670542707_18121573162715693_7373849723961909767_n.jpg",
  // Batch 4 (4 scatti — chiusura galleria)
  "/images/assets/6aqj4sf3_saveclip.app_670527165_18121573234715693_189906258532641890_n.jpg",
  "/images/assets/u608kaoo_saveclip.app_670415724_18121573225715693_3022510420254776164_n.jpg",
  "/images/assets/p99i7ayq_saveclip.app_670627358_18121573216715693_7394021842752888133_n.jpg",
  "/images/assets/bawmzc6z_saveclip.app_670982621_18121573207715693_4845747762510000836_n.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Finale Scudetto 2025/26 — Vero Volley Milano vs Imoco Conegliano — Scatto ${String(i + 1).padStart(2, "0")} di ${GALLERY_PHOTOS.length}`,
}));

const VeroVolley = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const idx = WORKS.findIndex((w) => w.to === "/works/vero-volley");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = WORKS[(idx + 1) % WORKS.length];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "OnSports × Vero Volley", url: "/works/vero-volley" },
  ]);

  return (
    <>
      <Seo
        title="Vero Volley: club Superlega volley italiana | OnSports Case"
        description="Il lavoro OnSports per Vero Volley, club di pallavolo Serie A italiana. Brand identity, content production, attivazione community digitale. 2025/2026."
        path="/works/vero-volley"
        image="/images/assets/l0oer29x_saveclip.app_670596620_18121573099715693_1474756804845084331_n.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/vero-volley/hero.jpg"
        imageAlt="Vero Volley Milano — Gold Partner OnSports"
        eyebrow="● Case study · Cultural Impact · Content & IP Studio"
        titleLines={["Una stagione vissuta da", "DENTRO."]}
        highlightWord="DENTRO."
        subtitle="Non un fornitore di contenuti. Gold Partner. OnSports ha attraversato la stagione 2025/26 del Vero Volley Milano da dentro lo spogliatoio e da dentro il pubblico — fino alla Finale Scudetto contro Imoco Conegliano all'Allianz Cloud — amplificando il racconto sulla community proprietaria Problemi di Volley."
        meta="Stagione 2025/2026  ·  Cliente — Numia Vero Volley Milano  ·  Categoria — Content & IP Studio"
        overlayOpacity={0.5}
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    OnSports è Gold Partner di Vero Volley Milano, una delle realtà più
                    importanti della pallavolo italiana, e del settore femminile in
                    particolare. Una partnership che ci posiziona dentro all'ecosistema
                    del club — non come fornitore esterno di servizi, ma come stakeholder
                    con interesse diretto sul racconto della stagione e sulla crescita
                    dell'audience del volley italiano.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Per la stagione 2025/26 abbiamo accompagnato la squadra femminile con
                    un programma editoriale continuativo: dalla presentazione ufficiale
                    del roster alla coverage di alcune delle partite chiave del
                    campionato, fino al reportage fotografico della Finale Scudetto
                    contro la Prosecco Doc Antonio Carraro Imoco Conegliano. Ogni
                    contenuto è stato amplificato sulla community proprietaria Problemi
                    di Volley — punto di riferimento culturale italiano per gli
                    appassionati di pallavolo, con oltre 87mila follower su Instagram.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">OnSports × Vero Volley — Stagione 2025/26</SchedaRow>
                    <SchedaRow label="Client">Numia Vero Volley Milano</SchedaRow>
                    <SchedaRow label="Ruolo">
                      Gold Partner del club<br />
                      Content production stagionale<br />
                      Reportage fotografico Finale Scudetto
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Community">
                      Problemi di Volley (87K+ Instagram)
                    </SchedaRow>
                    <SchedaRow label="Arena">Allianz Cloud — Milano</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — IL CONTESTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-contesto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>Il contesto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  La rivalità che sta riscrivendo il volley italiano.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il volley femminile italiano è il più forte d'Europa. Tre finaliste su
                  quattro dell'ultima Champions League sono italiane, e la Serie A1
                  Tigotà è ormai un campionato di livello mondiale. Al centro di questo
                  movimento c'è una rivalità che sta diventando un simbolo culturale
                  dello sport italiano contemporaneo: Numia Vero Volley Milano contro
                  Prosecco Doc Imoco Conegliano. La Finale Scudetto 2025/26 — la quarta
                  consecutiva tra le due squadre — è andata in scena fra l'11 e il 22
                  aprile 2026, davanti a palazzetti sold-out a Villorba e a Milano.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Per un club di questa scala servono contenuti capaci di stare dentro al
                  campionato a livello di gioco, ma anche di parlare al pubblico
                  mainstream che si sta avvicinando al volley femminile per la prima
                  volta — grazie all'effetto Egonu, alla qualità delle giocatrici e ai
                  numeri di crescita dell'audience. Era questo il perimetro entro cui
                  abbiamo costruito il nostro racconto della stagione.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL REPORTAGE / GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-reportage">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Il reportage</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Allianz Cloud, Milano. La Finale Scudetto.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
                  Allianz Cloud di Milano, Finale Scudetto Serie A1 Tigotà 2025/26 contro
                  Imoco Conegliano. Diciotto scatti per raccontare la partita non come un
                  risultato ma come un'esperienza: le giocatrici prima della battuta, lo
                  sguardo degli allenatori, il pubblico dell'Allianz Cloud che canta il
                  proprio amore per questa squadra, la fatica e l'attesa nei momenti
                  decisivi. Un reportage che vive a metà tra fotografia sportiva e
                  fotografia documentaria.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 grid-flow-row-dense gap-2"
            data-testid="gallery-grid"
          >
            {GALLERY_PHOTOS.map((photo, i) => {
              const span2 = (i + 1) % 5 === 0; // ogni 5° slot: 2x2 su desktop
              const wrapperClass = span2 ? "md:col-span-2 md:row-span-2" : "";
              const alt = LIGHTBOX_IMAGES[i].alt;
              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 8) * 0.03}
                  className={wrapperClass}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full aspect-square overflow-hidden relative group focus:outline-none"
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri scatto ${i + 1} di ${GALLERY_PHOTOS.length}`}
                  >
                    <img
                      src={photo}
                      alt={alt}
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Tre fasi. Un solo racconto.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
                  Il lavoro con il Vero Volley non è stata una singola attivazione, ma un
                  programma editoriale costruito sul ritmo della stagione sportiva. Lo
                  abbiamo articolato in tre fasi che si sono alimentate l'una con l'altra
                  — dalla presentazione del roster fino alla fine del playoff.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Presentazione squadra"
                body="Presenza diretta alla presentazione ufficiale del roster femminile del Vero Volley. Produzione di contenuti dedicati e amplificazione sulla community Problemi di Volley per introdurre le giocatrici al pubblico più ampio della pallavolo italiana."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Coverage di stagione"
                body="Programma continuativo di copertura editoriale delle partite chiave della stagione regolare e dei playoff sul canale Problemi di Volley. Storytelling che ha unito codici giornalistici e linguaggio nativo della community per generare conversazione attorno al club."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Reportage Finale Scudetto"
                body="Coverage fotografica dedicata alle partite della Finale Scudetto disputate all'Allianz Cloud di Milano contro Imoco Conegliano. Diciotto scatti che hanno raccontato giocatrici, panchina e pubblico, distribuiti sui canali OnSports e sulla community Problemi di Volley."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — IMPATTO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Una partnership che non si misura solo in numeri.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="Gold" label="Partner del Vero Volley Milano" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="87.000+" label="Follower community Problemi di Volley" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="18" label="Scatti reportage Finale Scudetto" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="2025/26" label="Stagione completa seguita" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Problemi di Volley è la community editoriale proprietaria di OnSports
            dedicata al volley italiano, con oltre 87mila follower su Instagram
            (@problemi_di_volley). Il reportage della Finale Scudetto è composto da 18
            scatti del fotografo, prodotti durante le partite della serie disputate
            all'Allianz Cloud di Milano nell'aprile 2026.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Posizione, non spazio
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Essere Gold Partner significa stare dentro al club — accesso, contesto,
                  relazioni. È quello che ha permesso a OnSports di lavorare con un
                  livello di profondità che un fornitore esterno non avrebbe mai
                  ottenuto, dentro un campionato top come la Serie A1 femminile.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Community come moltiplicatore
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Problemi di Volley non è un canale qualsiasi: è una community
                  editoriale costruita negli anni, con un tono riconoscibile, e con
                  87mila follower che si sono auto-selezionati come appassionati di
                  pallavolo. Per il Vero Volley significa parlare a un'audience nativa,
                  non comprata.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-dodgers">
                  Reportage come asset di brand
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-85 max-w-[40ch]">
                  Le 18 immagini della Finale Scudetto non sono uno scatto di servizio.
                  Sono fotografia documentaria — restano agli archivi del club, alla
                  community, ai media partner. Un asset che sopravvive alla partita e
                  diventa patrimonio narrativo della stagione.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CREDITS */}
      <section className="py-20 md:py-28 bg-dodgers text-galactico" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Client">Numia Vero Volley Milano</SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Gold Partner · Content production<br />
                  Reportage fotografico Finale Scudetto
                </SchedaRow>
                <SchedaRow label="Servizi">Content &amp; IP Studio</SchedaRow>
                <SchedaRow label="Community">
                  Problemi di Volley<br />
                  (community editoriale proprietaria OnSports)
                </SchedaRow>
                <SchedaRow label="Stagione">2025/2026</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[28ch]">
              Hai un club, una property o una squadra che vuole raccontarsi davvero?
              Costruiamo insieme il suo <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo come partner di club, leghe e federazioni che vogliono
              trasformare il proprio racconto in patrimonio narrativo. Con community
              proprietarie e formati editoriali pensati per la sport culture.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default VeroVolley;
